/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../registry/Manager';
import { page as SamplePage } from '../../help/sample_pages/TabsPage';
import { checkForFixedChild } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  BaseComponentItem,
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue, TabItem, WrapperProps } from './TabItem';

const name = 'Tab Item';

const TabItemManager: ComponentManagerFn<'TabItem', WrapperProps> = (
  platform: Platform,
): ComponentManager<'TabItem', WrapperProps> => {
  return {
    ...baseComponentManager<'TabItem', WrapperProps>(platform, 'TabItem'),
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          ...getCustomChildItemTypes('TabItem'),
          'ActionButton',
          'BoxContainer',
          'Button',
          'ButtonDropdown',
          'CanvasGrid',
          'Card',
          'Chartjs',
          'Code',
          'CombinationChart',
          'DataGrid',
          'DataStoreReference',
          'EmbedPage',
          'FAB',
          'FlexPanel',
          'Form',
          'HorizontalSplitPanel',
          'IconButton',
          'IoTImageMapper',
          'Markdown',
          'Mirror',
          'Paper',
          'ProgressIndicator',
          'PulseTip',
          'RichTextEditor',
          'RowPanel',
          'SearchForm',
          'SmartSearch',
          'SpeedDial',
          'SuspendPage',
          'TabPanel',
          'Toolbar',
          'TriggerOnTabActive',
          'TriggerOnTabExit',
          'VerticalSplitPanel',
          'WorkflowViewer',
          'TriggerOnClick',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    props: {
      label: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      hotKey: {
        label: 'Hot Key',
        type: 'string',
        maxLength: 1,
        helpText: (item: RequiredItem<'TabItem'>) => {
          const key =
            item.props.hotKey ||
            (item.props.label.type === 'text' && item.props.label.value?.length
              ? item.props.label.value.charAt(0)
              : '<Hot Key>');

          return `A shortcut key to be mapped for this tab item click event. User can use Ctrl+Shift+${key} or Cmd+Shift+${key} as a keyboard shortcut instead of a mouse click on this tab item.`;
        },
      },
      wrapped: {
        label: 'Wrapped',
        type: 'boolean',
        helpText: 'If checked the label will be wrapped',
      },
      useImageAsIcon: {
        label: 'Use Image as Icon',
        type: 'boolean',
        helpText: 'Use image as Tab Icon',
      },
      icon: {
        label: 'Start Icon',
        type: 'iconselect',
      },
      image: {
        label: 'Start Image',
        type: 'ImgAttributeChooser',
      },
      imageHeight: {
        label: 'Start Image Height',
        type: 'number',
      },
      imageWidth: {
        label: 'Start Image Width',
        type: 'number',
      },
      indicatorColor: {
        label: 'Indicator Color',
        type: 'ThemeBGColor',
        filterColor: (color) => color !== 'paper',
        helpText:
          'The color of tab header text and active indicator when selected. Choose Inherit to hide the active indicator and set the tab header text color to text.primary.',
      },
      subtitle: {
        label: 'Sub Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      badge: {
        label: 'Badge',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'The badge /tag for the tab',
      },
    },
    name,
    canHaveChildren: () => true,
    category: 'Containers',
    excludeChildren: true,
    updateMetaData: (
      { itemId }: BaseComponentItem,
      data: Required<TabItem>,
    ) => ({
      ...data,
      value: itemId,
    }),
    getWrapperProps: ({ item }) => ({ value: item.itemId }),
    allowClick: true,
    layout: 'sizeAware',
    getDisplayText: (item) => item.props.label.value,
    generateShortId: (item) =>
      item.label?.type === 'text' ? item.label.value : undefined,
    icon: { iconName: 'io-tab-item', iconType: 'cloudio' },
    appendOverlay: true, // tab item button's first child has bottom margin and hence move the overlay toward the end
    description: `**Overview**<br/>
The Tab Item represents a single, distinct section within a Tab Panel. Think of it as an individual folder in a filing cabinet; it defines both the "label" on the folder (the header) and the "papers" inside it (the content).
By adding multiple Tab Items to a Tab Panel, you create a navigational interface that allows users to switch between different views—such as "Details," "History," and "Settings"—without leaving the current page context.

**1. Identity & Labeling**<br/>
The header of the tab is the user's primary navigation cue. You can configure it to display rich, dynamic information.
* **Title:** Defines the primary text label of the tab. This property supports expressions, allowing you to create dynamic labels (e.g., binding the title to a user's name: $.session.userName).
* **Sub Title:** Displays a secondary line of text below the main title. Use this to add context, such as a status (e.g., "Active") or a category.
* **Badge:** Renders a notification badge (chip) next to the title. This is ideal for displaying counts, such as "Messages (3)" or "Alerts (5)." It accepts dynamic expressions to update the count in real-time.
* **Wrapped:** If enabled, long tab titles will wrap onto a second line instead of being truncated with an ellipsis.

**2. Visual Customization**<br/>
You can tailor the appearance of each tab to distinguish it or match specific branding requirements.
* **Start Icon:** Adds a standard vector icon to the left of the title, helping users visually identify the tab's purpose (e.g., a "Gear" icon for Settings).
* **Use Image as Icon:** Toggle this to replace the standard icon with a custom image.
* **Start Image:** The source URL for the custom image.
* **Image Dimensions:** Use Height and Width to control the size of the custom image.
* **Indicator Color:** Customizes the color of the active tab indicator (the underline or highlight) and the text for this specific tab. (Usage: Use this to highlight critical tabs, e.g., setting the "Error Logs" tab indicator to Red).

**3. Accessibility & Shortcuts**<br/>
Enhance the usability of your application for power users by enabling keyboard navigation.
* **Hot Key:** Assigns a specific keyboard shortcut to activate this tab.
* **Behavior:** If you assign the letter S, the user can press Ctrl + Shift + S (Windows/Linux) or Cmd + Shift + S (Mac) to instantly switch to this tab without using the mouse.

**4. Content Composition**<br/>
The Tab Item serves as a wrapper for the actual UI elements displayed when the tab is active.
* **Container Behavior:** By default, a Flex Panel is added as the root child. You can place any combination of components—Forms, Grids, Charts, or Layouts—inside this container.
* **Lazy Rendering:** The content inside a Tab Item is typically rendered only when the tab becomes active, optimizing performance for complex pages.`,
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'image':
        case 'imageHeight':
        case 'imageWidth':
          return props.useImageAsIcon;
        case 'icon':
          return !props.useImageAsIcon;
        default:
          return true;
      }
    },
    helpRenders: [
      {
        title: 'Basic Tab',
        description: 'Tab with a Title & Icon',
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: ['icon', 'label'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Indicator Color',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['indicatorColor'],
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
            indicatorColor: 'success',
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Sub Title',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['subtitle'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
            subtitle: { type: 'text', value: 'Tab with a sub title!' },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Dynamic Title & Sub Title using Expression',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['label', 'subtitle'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'expression', value: '$.session.displayName' },
            icon: { iconName: 'apple', iconType: 'brands' },
            subtitle: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Hi ${$.session.displayName}`',
            },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Tab with Badge',
        page: SamplePage,
        exampleId: 'e5',
        visibleProps: ['badge'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
            badge: { type: 'expression', value: '11 * 11' },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
    ],
  };
};

export default TabItemManager;
